import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { ActiveYearProvider } from './contexts/ActiveYearContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { authConfig } from './config/auth';
import ErrorBoundary from './components/common/ErrorBoundary';
import DevDateBadge from './components/dev/DevDateBadge';
import { initDevTodayFromUrl } from './lib/devClock';
import './index.css';

// Configure Amplify with auth settings
Amplify.configure(authConfig);

// Apply a `?devToday=YYYY-MM-DD` simulated date before the first render so
// date-driven UI reads it immediately. Dev hosts only; inert in production.
initDevTodayFromUrl();

// Every page is a lazy-loaded chunk with a content hash in its filename, and
// the chunks are served from inside the Lambda image — so a deploy deletes the
// old hashed files. A tab opened before the deploy then fails to load chunks
// when navigating. Vite reports that failure as `vite:preloadError`; reload
// once to pick up the fresh index.html (and its new chunk names). The
// timestamp guard stops a reload loop if reloading doesn't fix it.
window.addEventListener('vite:preloadError', (event) => {
  const RELOAD_GUARD_KEY = 'chunk-reload-at';
  const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
  if (Date.now() - lastReload < 30_000) return; // already tried — let the error surface
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  event.preventDefault(); // suppress the unhandled-rejection; we're reloading instead
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ActiveYearProvider>
              <BrandingProvider>
                <App />
                <DevDateBadge />
              </BrandingProvider>
            </ActiveYearProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
